import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  WorkflowEmailSession,
  WorkflowEmailRecipient,
} from "../../types/workflow";
import { workflowService } from "../../services/workflowService";
import { useMobile } from "../../@/hooks/use-mobile";
import { FiX, FiChevronRight, FiRefreshCw } from "react-icons/fi";

// Import the sidebar.css which contains our brutalist design tokens
import "../../styles/sidebar.css";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const ChevronRightIcon = FiChevronRight as React.FC<React.SVGProps<SVGSVGElement>>;
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;

interface WorkflowEmailDetailModalProps {
  session: WorkflowEmailSession;
  onClose: () => void;
}

const WorkflowEmailDetailModal: React.FC<WorkflowEmailDetailModalProps> = ({
  session,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<WorkflowEmailRecipient[]>([]);
  const [counts, setCounts] = useState({
    sent: 0,
    delivered: 0,
    opened: 0,
    failed: 0,
  });
  const [showEmailContent, setShowEmailContent] = useState(false);
  const isMobile = useMobile();

  useEffect(() => {
    loadSessionDetails();
  }, [session.id]);

  const loadSessionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("[EmailDetail] Loading details for session:", session.id);
      const details = await workflowService.getEmailSessionDetails(session.id);
      console.log("[EmailDetail] Details:", details);
      setRecipients(details.results || []);
      setCounts(details.counts);
    } catch (err: any) {
      console.error("Failed to load email session details:", err);
      setError(err?.message || "Failed to load email details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getRecipientStatus = (recipient: WorkflowEmailRecipient) => {
    if (!recipient.success) {
      return (
        <span className="text-red-400 text-sm font-medium">
          Failed
          {recipient.error_message && (
            <span
              className="ml-1 text-xs text-red-400/70"
              title={recipient.error_message}
            >
              ({recipient.error_code || "Error"})
            </span>
          )}
        </span>
      );
    }
    if (recipient.opened_at) {
      return <span className="text-purple-400 text-sm font-medium">Opened</span>;
    }
    return <span className="text-[#4ade80] text-sm font-medium">Delivered</span>;
  };

  // Stats card component
  const StatCard: React.FC<{
    value: number;
    label: string;
    colorClass: string;
    bgStyle?: React.CSSProperties;
  }> = ({ value, label, colorClass, bgStyle }) => (
    <div
      className="p-4 text-center"
      style={{
        backgroundColor: "#0a0a0a",
        border: "1px solid var(--su-brutalist-border)",
        ...bgStyle,
      }}
    >
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
      <div className={`text-sm ${colorClass} opacity-70`}>{label}</div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-80" onClick={onClose} />

      <div className="relative z-10 flex min-h-full items-start justify-center p-3 sm:p-4">
        {/* Modal Container */}
        <div
          className="su-brutalist-container w-11/12 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          style={{ backgroundColor: "var(--su-brutalist-bg)" }}
        >
          {/* Header */}
          <div
            className="flex items-start justify-between p-4"
            style={{ borderBottom: "1px solid var(--su-brutalist-border)" }}
          >
            <div className="flex-1 min-w-0 mr-4">
              <h3 className="text-xl font-semibold text-white truncate">
                {session.email_subject || "No Subject"}
              </h3>
              <p className="text-sm text-white/50 mt-1">
                Sent on {formatDate(session.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 flex-shrink-0"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12 flex-grow">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
              <span className="ml-3 text-white/70">Loading details...</span>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div
              className="mx-4 mt-4 text-sm p-3"
              style={{
                backgroundColor: "rgba(127, 29, 29, 0.3)",
                border: "1px solid #7f1d1d",
              }}
            >
              <p className="text-red-400">{error}</p>
              <button
                onClick={loadSessionDetails}
                className="mt-2 text-sm text-red-400 hover:text-red-300 underline flex items-center gap-1"
              >
                <RefreshIcon className="w-3 h-3" />
                Try again
              </button>
            </div>
          )}

          {/* Content */}
          {!loading && !error && (
            <div className="overflow-y-auto flex-grow p-4 space-y-6">
              {/* Stats Cards */}
              <div className={`grid ${isMobile ? "grid-cols-2" : "grid-cols-4"} gap-4`}>
                <StatCard value={counts.sent} label="Sent" colorClass="text-white" />
                <StatCard
                  value={counts.delivered}
                  label="Delivered"
                  colorClass="text-[#4ade80]"
                  bgStyle={{ borderColor: "rgba(74, 222, 128, 0.2)" }}
                />
                <StatCard
                  value={counts.opened}
                  label="Opened"
                  colorClass="text-purple-400"
                  bgStyle={{ borderColor: "rgba(192, 132, 252, 0.2)" }}
                />
                <StatCard
                  value={counts.failed}
                  label="Failed"
                  colorClass="text-red-400"
                  bgStyle={{ borderColor: "rgba(248, 113, 113, 0.2)" }}
                />
              </div>

              {/* Recipients Section */}
              <div>
                <h4 className="text-lg font-medium text-white mb-3">
                  Recipients ({recipients.length})
                </h4>
                {recipients.length === 0 ? (
                  <p className="text-white/50 text-sm">No recipients found</p>
                ) : isMobile ? (
                  // Mobile Card Layout
                  <div className="space-y-2">
                    {recipients.map((recipient, index) => (
                      <div
                        key={`${recipient.send_session_id}-${recipient.email}-${index}`}
                        className="p-3"
                        style={{
                          backgroundColor: "#0a0a0a",
                          border: "1px solid var(--su-brutalist-border)",
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-white truncate">
                            {recipient.email}
                          </span>
                          {getRecipientStatus(recipient)}
                        </div>
                        {recipient.contact_name && (
                          <div className="text-xs text-white/50">
                            {recipient.contact_name}
                          </div>
                        )}
                        <div className="text-xs text-white/40 mt-1">
                          Sent: {formatDate(recipient.sent_at)}
                          {recipient.opened_at && (
                            <span className="ml-2">
                              Opened: {formatDate(recipient.opened_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop Table Layout
                  <div
                    className="overflow-x-auto"
                    style={{ border: "1px solid var(--su-brutalist-border)" }}
                  >
                    <table className="min-w-full">
                      <thead style={{ backgroundColor: "#0a0a0a" }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Sent At
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                            Opened At
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((recipient, index) => (
                          <tr
                            key={`${recipient.send_session_id}-${recipient.email}-${index}`}
                            className="hover:bg-white/5"
                            style={{
                              borderTop:
                                index > 0
                                  ? "1px solid rgba(255, 255, 255, 0.08)"
                                  : undefined,
                            }}
                          >
                            <td className="px-4 py-3 text-sm text-white">
                              {recipient.email}
                            </td>
                            <td className="px-4 py-3 text-sm text-white/60">
                              {recipient.contact_name || "-"}
                            </td>
                            <td className="px-4 py-3">{getRecipientStatus(recipient)}</td>
                            <td className="px-4 py-3 text-sm text-white/60 whitespace-nowrap">
                              {formatDate(recipient.sent_at)}
                            </td>
                            <td className="px-4 py-3 text-sm text-white/60 whitespace-nowrap">
                              {formatDate(recipient.opened_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Email Content Section */}
              {session.email_body && (
                <div>
                  <button
                    onClick={() => setShowEmailContent(!showEmailContent)}
                    className="flex items-center text-lg font-medium text-white mb-3 hover:text-white/80"
                  >
                    <ChevronRightIcon
                      className={`w-5 h-5 mr-2 transition-transform ${
                        showEmailContent ? "rotate-90" : ""
                      }`}
                    />
                    Email Content
                  </button>
                  {showEmailContent && (
                    <div
                      className="p-4 max-h-96 overflow-y-auto"
                      style={{
                        backgroundColor: "#0a0a0a",
                        border: "1px solid var(--su-brutalist-border)",
                      }}
                    >
                      <div
                        className="prose prose-invert prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: session.email_body }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div
            className="p-4 flex justify-end"
            style={{ borderTop: "1px solid var(--su-brutalist-border)" }}
          >
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkflowEmailDetailModal;
